import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { homeApi } from '../api/home';
import BottomNav from '../components/BottomNav';
import Navbar from '../components/Navbar';
import './ArticleDetail.css';

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    if (id) {
      fetchArticleDetail();
      fetchComments();
    }
  }, [id]);

  const fetchArticleDetail = async () => {
    try {
      setLoading(true);
      const result = await homeApi.getArticleDetail({ id });
      if (result.code === 200 || result.code === 1) {
        setArticle(result.data);
        setCommentCount(result.data.comment_num || 0);
      } else {
        throw new Error(result.msg || '获取文章详情失败');
      }
      setLoading(false);
    } catch (error) {
      console.error('获取文章详情失败:', error);
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      setLoadingComments(true);
      const result = await homeApi.getArticleComments({ id, page: 1 });
      if (result.code === 200 || result.code === 1) {
        setComments(result.data.list || []);
        setCommentCount(result.data.total || 0);
      } else {
        throw new Error(result.msg || '获取评论失败');
      }
      setLoadingComments(false);
    } catch (error) {
      console.error('获取评论失败:', error);
      setLoadingComments(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim()) {
      alert('请输入评论内容');
      return;
    }

    try {
      setSubmittingComment(true);
      const result = await homeApi.addArticleComment({
        article_id: id,
        content: commentText,
        pid: 0
      });
      if (result.code === 200 || result.code === 1) {
        setCommentText('');
        fetchComments();
        alert('评论提交成功');
      } else {
        alert(result.msg || '评论提交失败，请重试');
      }
      setSubmittingComment(false);
    } catch (error) {
      console.error('提交评论失败:', error);
      alert('评论提交失败，请重试');
      setSubmittingComment(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleHome = () => {
    navigate('/');
  };

  const handleLikeArticle = async () => {
    if (!article) return;
    
    // 调用点赞API
    try {
      const result = await homeApi.likeArticle({ id });
      if (result.code === 200 || result.code === 1) {
        setArticle(prev => ({
          ...prev,
          like_num: result.is_liked ? prev.like_num + 1 : prev.like_num - 1,
          is_liked: result.is_liked
        }));
      }
    } catch (error) {
      console.error('点赞失败:', error);
      // 错误时使用模拟点赞
      setArticle(prev => ({
        ...prev,
        like_num: prev.is_liked ? prev.like_num - 1 : prev.like_num + 1,
        is_liked: !prev.is_liked
      }));
    }
  };

  const handleLikeComment = async (commentId, isChild = false, parentId = null) => {
    // 这里应该调用点赞评论的API
    // const result = await homeApi.likeComment({ id: commentId });
    // if (result.code === 200 || result.code === 1) {
    //   // 更新评论点赞状态
    // }
    
    // 模拟点赞
    setComments(prev => prev.map(comment => {
      if (comment.id === commentId && !isChild) {
        return {
          ...comment,
          like_num: comment.is_like ? comment.like_num - 1 : comment.like_num + 1,
          is_like: !comment.is_like
        };
      } else if (comment.id === parentId && isChild) {
        return {
          ...comment,
          child: comment.child.map(child => {
            if (child.id === commentId) {
              return {
                ...child,
                like_num: child.is_like ? child.like_num - 1 : child.like_num + 1,
                is_like: !child.is_like
              };
            }
            return child;
          })
        };
      }
      return comment;
    }));
  };

  if (loading) {
    return <div className="loading">加载中...</div>;
  }

  if (!article) {
    return <div className="empty">文章不存在</div>;
  }

  return (
    <div className="article-detail">
      <Navbar showHome={false} />

      {/* 文章内容 */}
      <div className="content-area">
        {/* 文章标题 */}
        <div className="article-title">
          {article.title}
        </div>

        {/* 文章元信息 */}
        <div className="article-meta">
          <span className="category">{article.category_name}</span>
          <span className="divider">·</span>
          <span className="time">{article.create_time}</span>
          <span className="divider">·</span>
          <span className="views">
            👁️ {article.view_num}
          </span>
        </div>

        {/* 文章作者 */}
        <div className="article-author">
          <div className="author-info">
            <div className="author-avatar">
              <img src={article.avatar || '/avatar.jpg'} alt={article.author} />
            </div>
            <div className="author-name">
              {article.author}
            </div>
          </div>
        </div>

        {/* 文章内容 */}
        <div className="article-content">
          <div dangerouslySetInnerHTML={{ __html: article.content }} />
        </div>

        {/* 文章图片 */}
        {article.images && article.images.length > 0 && (
          <div className="article-images">
            {article.images.map((image, index) => (
              <img key={index} src={image} alt={`图片 ${index + 1}`} />
            ))}
          </div>
        )}

        {/* 文章统计 */}
        <div className="article-stats">
          <div className="stat-item">
            <span className="stat-icon">👍</span>
            <span className="stat-text">{article.like_num}</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">💬</span>
            <span className="stat-text">{article.comment_num}</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">👁️</span>
            <span className="stat-text">{article.view_num}</span>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="action-buttons">
          <button 
            className={`action-button like-button ${article.is_liked ? 'liked' : ''}`}
            onClick={handleLikeArticle}
          >
            <span className="button-icon">{article.is_liked ? '❤️' : '🤍'}</span>
            <span className="button-text">{article.is_liked ? '已点赞' : '点赞'}</span>
          </button>
          <button className="action-button share-button">
            <span className="button-icon">📤</span>
            <span className="button-text">分享</span>
          </button>
        </div>

        {/* 评论区 */}
        <div className="comments-section">
          <h3 className="comments-title">
            评论 ({commentCount})
          </h3>

          {/* 评论列表 */}
          <div className="comments-list">
            {loadingComments ? (
              <div className="loading-comments">加载评论中...</div>
            ) : comments.length > 0 ? (
              comments.map((comment, index) => (
                <div key={comment.id} className="comment-item">
                  <div className="comment-header">
                    <div className="comment-author">
                      <img src={comment.avatar || '/avatar.jpg'} alt={comment.nickname} />
                      <span className="author-name">{comment.nickname}</span>
                    </div>
                    <div className="comment-time">
                      {comment.create_time}
                    </div>
                  </div>
                  <div className="comment-content">
                    {comment.content}
                  </div>
                  <div className="comment-actions">
                    <button 
                      className={`comment-action like-action ${comment.is_like ? 'liked' : ''}`}
                      onClick={() => handleLikeComment(comment.id)}
                    >
                      {comment.is_like ? '❤️' : '🤍'} {comment.like_num}
                    </button>
                    <button className="comment-action reply-action">
                      💬 回复
                    </button>
                  </div>
                  
                  {/* 子评论 */}
                  {comment.child && comment.child.length > 0 && (
                    <div className="child-comments">
                      {comment.child.map((childComment, childIndex) => (
                        <div key={childComment.id} className="child-comment">
                          <div className="child-comment-header">
                            <span className="child-author-name">{childComment.nickname}</span>
                            <span className="child-comment-time">{childComment.create_time}</span>
                          </div>
                          <div className="child-comment-content">
                            {childComment.content}
                          </div>
                          <div className="child-comment-actions">
                            <button 
                              className={`child-comment-action like-action ${childComment.is_like ? 'liked' : ''}`}
                              onClick={() => handleLikeComment(childComment.id, true, comment.id)}
                            >
                              {childComment.is_like ? '❤️' : '🤍'} {childComment.like_num}
                            </button>
                            <button className="child-comment-action reply-action">
                              💬 回复
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-comments">暂无评论，快来发表你的看法吧！</div>
            )}
          </div>

          {/* 评论输入框 */}
          <div className="comment-input-section">
            <input
              type="text"
              className="comment-input"
              placeholder="写下你的评论..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              maxLength={200}
            />
            <button 
              className="comment-submit"
              onClick={handleSubmitComment}
              disabled={submittingComment}
            >
              {submittingComment ? '提交中...' : '发布'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetail;